import { useMemo } from "react";

import { useCenter, useCenterCourses } from "./useCenter";
import type { CenterCourse } from "../domain/Center";

interface NamedStudent {
    first_name: string;
    last_name?: string | null;
    course_id?: string | null;
}

export interface CourseLabels {
    /** The course's internal name, or null outside a training centre or with no course. */
    courseOf: (courseId: string | null | undefined) => string | null;
    /** "First Last · COURSE" in a training centre; just the name everywhere else. */
    labelOf: (student: NamedStudent) => string;
}

/** The pure part, so it can be tested without a centre. */
export function courseLabelsOf(courses: CenterCourse[]): CourseLabels {
    const names = new Map(courses.map((course) => [course.id, course.internal_name]));
    const courseOf = (courseId: string | null | undefined) =>
        (courseId && names.get(courseId)) || null;
    return {
        courseOf,
        labelOf: (student) => {
            const name = `${student.first_name} ${student.last_name ?? ""}`.trim();
            const course = courseOf(student.course_id);
            return course ? `${name} · ${course}` : name;
        },
    };
}

/**
 * Which course a student is on, by its internal name. A training centre's
 * student is enrolled once per course, so a name alone does not say which
 * enrolment a list or a picker means.
 */
export const useCourseLabels = (): CourseLabels => {
    const { center } = useCenter();
    const isTraining = center?.type === "training";
    const { courses } = useCenterCourses(center?.id, isTraining);
    // Keyed on what the names are, not on the array: the course list is a
    // fresh `[]` on every render until it loads (and always outside a training
    // centre), and callers fetch in effects that depend on these functions.
    const key = isTraining
        ? courses.map((course) => `${course.id}\u0000${course.internal_name}`).join("\u0001")
        : "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const labels = useMemo(() => courseLabelsOf(isTraining ? courses : []), [key]);
    return labels;
};
